import React from 'react';
import SearchingTheWeb from '../assets/searching_the_web.png';
import Bank from '../assets/bank.png';
import { Footnote, FootnoteRef, FootNoteRefProps_t } from '../components/FootNote';
import { InlineMath } from 'react-katex';

export const SearchAttackPage: React.FC<{}> = () => {

    // These footnotes are a little hacky. If you want your footnote to be in order,
    // you have to add it to this list in the order you want it.
    const references: Array<FootNoteRefProps_t> = [
        { id: 'rag', ref: "https://arxiv.org/pdf/2005.11401" },
        { id: 'selfcite', ref: "https://arxiv.org/pdf/2502.09604" },
        { id: 'longcite', ref: "https://arxiv.org/pdf/2409.02897" },
        { id: 'phantom', ref: "https://arxiv.org/pdf/2405.20485" },
        { id: 'trojanrag', ref: "https://arxiv.org/pdf/2405.13401" },
        { id: "glue", ref: "https://arxiv.org/pdf/2406.19417" },
        { id: 'ctrlrag', ref: "https://arxiv.org/pdf/2503.06950" },
        { id: 'poisonrag', ref: "https://arxiv.org/pdf/2402.07867" },
        { id: "anthropic_bm25", ref: "https://www.anthropic.com/engineering/contextual-retrieval"},
        { id: 'pagerank', ref: "https://en.wikipedia.org/wiki/PageRank"},
        { id: 'shannonmaxim', ref: "https://en.wikipedia.org/wiki/Kerckhoffs%27s_principle"},
        { id: 'google_embeddings', ref: "https://ai.google.dev/gemini-api/docs/embeddings"},
        { id: 'openai_embeddings', ref: "https://developers.openai.com/api/docs/guides/embeddings/"},
        { id: "anthropic_embeddings", ref: "https://platform.claude.com/docs/en/build-with-claude/embeddings"},
        { id: "cohere_rerank", ref: "https://docs.cohere.com/reference/rerank"},
        { id: "zerank_rerank", ref: "https://huggingface.co/zeroentropy/zerank-1"},
        { id: "stemming", ref: "https://nlp.stanford.edu/IR-book/html/htmledition/stemming-and-lemmatization-1.html"},
        { id: "HNSW", ref: "https://arxiv.org/pdf/1603.09320"},
        { id: "PQ", ref: "https://inria.hal.science/inria-00514462v2/document"},
        { id: "milvus", ref: "https://github.com/milvus-io/milvus"},
        { id: "pinecone", ref: "https://github.com/pinecone-io"},
        { id: "weaviate", ref: "https://github.com/weaviate/weaviate"},    
        { id: "google_api", ref: "https://developers.google.com/custom-search/v1/overview"},
        { id: "bing_api", text: "it looks like Bing retired their search API and then brought it back for use in RAG. I think this would work fine.", ref: "https://www.microsoft.com/en-us/bing/apis/grounding-pricing"},
        { id: 'serp', ref: "https://blog.google/innovation-and-ai/technology/safety-security/serpapi-lawsuit/"},
        { id: 'usage', ref: "https://techcrunch.com/2026/02/27/chatgpt-reaches-900m-weekly-active-users/"},      
        { id: 'Google_AI_Usage', ref: "https://techcrunch.com/2025/04/25/googles-ai-search-numbers-are-growing-and-thats-by-design/"},      
        { id: "Rainie 2025", ref: "https://imaginingthedigitalfuture.org/wp-content/uploads/2025/03/ITDF-LLM-User-Report-3-12-25.pdf"},
        { id: 'SEO', ref: "https://www.grandviewresearch.com/industry-analysis/seo-software-market-report"},      
    ];

    // This gets the reference number that we render next to the footnote.
    // It just finds the index of the footnote with the given id in the list.
    const f = React.useCallback((id: string) => {
        let index = references.findIndex(ref => ref.id === id);
        if (index == -1) {
            throw Error(`could not find reference: ${id} in list`);
        }
        return index + 1;
    }, [references]);

    return (
        <main>
            <h1>Poisoning Chatbot RAG Queries via Automated Reverse-Engineering</h1>
            <p>
                The most used chatbot products (Google
                Search with AI summary, Gemini, ChatGPT, Claude etc.) are
                deployed in a retrieval augmented generation (RAG)<Footnote id="rag" f={f} /> system setup.
            </p>
            <p>
                In a RAG setup, the large language model (LLM) may choose to query a search index
                for additional context to aid in answering the question. The index often includes
                documents from the internet.
            </p>
            <div style={{ textAlign: 'center' }}>
                <img src={SearchingTheWeb} alt={"ChatGPT saying \"Searching the Web\""} style={{ width: '100%' }} />
            </div>
            <p>
                Then text from the documents in that query will be given to the LLM. The LLM is trained so
                the content is cited in the response.<Footnote id='selfcite' f={f} /> <Footnote id='longcite' f={f} /></p>
            <div style={{ textAlign: 'center' }}>
                <img src={Bank} alt={"ChatGPT giving Bank of America support phone number results"} style={{ width: '100%' }} />
            </div>
            <p>
                Existing work has shown these systems can be attacked with poisoned documents.
                The basic attack is
                <ol>
                    <li>Pick a query to poison <InlineMath math="q" /> like "What is Bank of America's phone number?"</li>
                    <li>Construct a document <InlineMath math="W'" /> that ranks high enough in the search for <InlineMath math="q" /> to be selected</li>
                </ol>
                Some attacks assume white-box<Footnote id="phantom" f={f} /> <Footnote id="trojanrag" f={f} /> or grey-box<Footnote id="glue" f={f}/> access to the ranking algorithm, which is unrealistic. 
                CtrlRAG<Footnote id="ctrlrag" f={f} /> assumes black-box
                access but repeatedly injects documents with modified text and re-queries the system to learn the ranking algorithm. That access-pattern could lead to detection by a defender. And PoisonedRAG<Footnote id="poisonrag" f={f} /> generates static content "by concatenating user queries to malicious documents to
                increase retrieval similarity"<Footnote id="ctrlrag" f={f} />. 
            </p>
            <p>
                In reality search ranking systems will change over time, can be a hybrid of attribute and vector search<Footnote id="anthropic_bm25" f={f}/>, and 
                include other ranking criteria 
                like document metadata<Footnote id="pagerank" f={f} />. Additionally defenders may have some methods for detecting attacks: "existing attack methods 
                often produce malicious documents with detectable anomalies, such as unnatural language patterns or repetitive structures, which can be 
                mitigated by basic filtering methods such as perplexity or pattern matching."<Footnote id="ctrlrag" f={f}/> An attack that is succesful 
                today may be thwarted by a detection 
                method the defender adds tomorrow.
            </p>
            <h4>Security Through Obscurity?</h4>
            <p>
                It's clear that having white-box access to the ranking algorithm makes RAG poisoning much easier. I would say 
                this is an example of Security through Obscurity. Which is generally considered a security anti-pattern.<Footnote id="shannonmaxim" f={f} />.
            </p>
            <h4>Reverse-Engineering with AI Agents</h4>
            <p>
                I propose an attack where AI agents are used to efficiently reverse engineer the ranking algorithm. This attack would be adaptive to 
                any changes in the ranking algorithm, because it can be run again. The attack would also have the potential to be more robust to varied 
                search techniques and anomaly detection defenses.
                <ol>
                    <li>Pick a query to poison <InlineMath math="q" /> like "What is Bank of America's phone number?"</li>
                    <li>
                        Query the RAG system with query <InlineMath math="q_1"/> and record the cited documents <InlineMath math="D_1=\{W_1^1, W_2^2, ...\}"/>
                    </li>
                    <li>
                        Repeat on multiple queries and construct a dataset <InlineMath math="\mathcal{D} = \{(q_1,D_1), (q_2,D_2), ... (q_n, D_n)\}"/>
                    </li>
                    <li>
                        Construct an AI agent that attempts to create a ranking algorithm that most closely matches <InlineMath math="\mathcal{D}"/>. Let <InlineMath math="m"/> copies of the agent run.
                    </li>
                    <li>
                        Take the top <InlineMath math="k<m"/> best performing ranking algorithms and construct <InlineMath math="k"/> poisoned 
                        documents <InlineMath math="\{W_1', W_2', ... W_k'\}"/> that rank highly for <InlineMath math="q" />.
                    </li>
                </ol>
            </p>
            <h4>Details</h4>
            <p>
                The agents don't have to reverse engineer the ranking algorithm perfectly, just well enough that one of <InlineMath math="\{W_1', W_2', ... W_k'\}"/> gets cited.
            </p>
            <p>
                To make the search more efficient, the attacker could provide the agents with search ranking primitives. All of the major chatbot providers 
                have embedding APIs<Footnote id="google_embeddings" f={f}/> <Footnote id="openai_embeddings" f={f}/>  <Footnote id="anthropic_embeddings" f={f}/>. It seems likely 
                those are used in their ranking algorithms. For example if targeting ChatGPT, the attacker could give the AI agent the OpenAI Embedding API.
            </p>
            <p>
                Often vector embedding search is combined with a full text search algorithm like BM-25<Footnote id="anthropic_bm25" f={f}/> and 
                some re-ranking model<Footnote id="cohere_rerank" f={f}/> <Footnote id="zerank_rerank" f={f}/>. There's also 
                variations on chunking, stemming<Footnote id="stemming" f={f}/>, what approximate nearest neighbor algorithm<Footnote id="HNSW" f={f}/> <Footnote id="PQ" f={f}/> is used, 
                and what parameters are used in the algorithm (cluster size, number of clusters probed etc.). All of these can affect the final rankings.
                There's multiple open source vector search databases<Footnote id="milvus" f={f}/> <Footnote id="pinecone" f={f}/> <Footnote id="weaviate" f={f}/> that an attacker
                could provide to the agents so the agents don't have to build a vector database.
            </p>
            <p>
                A different approach could be to create a learned algorithm that approximates the ranking algorithm. I'm not sure if the result would be as interesting if 
                the ranking algorithm approximation is just learned rather than reverse-engineered.
            </p>
            <p>
                One small subtletly is that agents need to have access to documents that weren't chosen for a query <InlineMath math="q_1"/> that can be 
                compared against the chosen documents <InlineMath math="D_1"/>. The attacker could use the Google Search API<Footnote id="google_api" f={f}/> or Bing Search API<Footnote id="bing_api" f={f}/> to 
                find documents that are relevant to <InlineMath math="q_1"/> but weren't chosen.
            </p>
            <p>
                If none of <InlineMath math="\{W_1', ... W_k'\}"/> work, the attacker could update the dataset and try again. Multiple agents could try starting from the 
                best ranking algorithms of the previous generation. Repeatedly doing this could lead to the defender detecting the attack. 
            </p>
            <h4>Defenses</h4>
            <p>
                Defenders could use filters such that certain queries are only allowed to use a trusted subset of the documents. 
                For example if a user is asking about financial information, the cited sources can only come from 
                trusted domains. This is similar to applying the filter "site: .edu" to a Google Search. This may not scale to every category of question.
            </p>
            <p>
                The defender may notice abnormal access-patterns while <InlineMath math="\mathcal{D}"/> is being constructed. For example Google recently took legal action against a company scraping their search results.<Footnote id="serp" f={f}/>
            </p>
            <h4>Implications</h4>
            <p>
                Chatbots like ChatGPT are used by 900 million people<Footnote id="usage" f={f}/> and Google AI 
                summarized search results are used by more than 1.5 billion people<Footnote id="Google_AI_Usage" f={f}/>. People rely on these systems for 
                important decisions one researcher found "37% reported getting help from the LLM treating an illness or medical condition" and "25% reported 
                getting help making a major investment or financial decision"<Footnote id="Rainie 2025" f={f}/>.
            </p> 
            <p>
                If these systems can be made to produce a response of 
                an attacker's choosing, the fallout could be far-reaching. 
            </p>
            <p>
                As long as search results from any web domain are permitted to be used as context in these systems, it seems difficult to prevent an attacker poisoning a query.
            </p>
            <h4>Broader Implications for Security Through Obscurity</h4>
            <p>
                If it's possible to efficiently reverse-engineer systems with AI agents like this, what other systems could be reversed and compromised more easily? 
                It seems such a system would only be vulnerable if 
                <ol>
                    <li>Successfully reverse-engineering the system has a clear signal. (In this case similarity of the agent's rankings to the RAG system's rankings <InlineMath math="\mathcal{D}"/>.)</li>
                    <li>Successfully reverse-engineering (or approximately doing so) gives the attacker an advantage. (In this case the ability to construct high-ranking poisoned documents <InlineMath math="\{W_1', ... W_k'\}"/>.)</li>
                </ol>
            </p>
            <p>
                Closely related to this is Google Search's ranking algorithm which is kept a secret and constantly changes. There's a $74.6 billion<Footnote id="SEO" f={f}/> industry 
                called Search Engine Optimization (SEO) for improving the search rankings of businesses. At my last startup we were on the second page 
                of Google results. We hired an SEO expert and within a couple months, we were the second result. The ranking algorithm constantly changes so 
                SEO is a continuous process.
            </p>
            <p>
                Social media content ranking algorithms seem eerily similar to this. Rather than targeting a query, the attacker might target an audience. 
                Could an attacker successfully reverse-engineer the ranking algorithm and create propaganda that is on the top of a targeted audience's feed? 
            </p>
            <p>
                The safety/security stack for many LLMs is not public. Ask a researcher at a frontier lab, 
                "Why isn't your safety training stack public?" they would probably say to prevent attackers defeating them. 
                Is that security through obscurity? Could that be overcome with an army of AI agents? It's not obviously "yes" to me but I'm not sure it's a "no" either.
            </p>
            <section id="footnotes">
                {references.map((item, i) => (
                    <FootnoteRef id={item.id} text={item.text} ref={item.ref} number={i + 1} />
                ))
                }
            </section>
        </main>
    )
};
